import { useCallback } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import {
  submitQuestionnaire,
  acceptAgreement,
  cancelAgreement,
  submitStatements,
} from "@/services/questionnaire";
import { WriteQuestionnaire } from "@/types/questionnaire";
import { ApiError } from "@/services/api";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { UseQuestionnaireHandlersProps } from "../context/QuestionnaireContext";
import { writeQuestionnaire } from "@/schemas/questionnaire";

export const useQuestionnaireHandlers = ({
  agreementId,
  token,
  agreementStatus,
  setCurrentStep,
  setIsAccepted,
  setSummaryPdfBase64,
  openDialog,
  closeDialog,
  setPersistedFormData,
  questionnaire,
}: UseQuestionnaireHandlersProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const submitQuestionnaireMutation = useMutationWithErrorHandling<
    { pdfData: string },
    ApiError,
    WriteQuestionnaire,
    unknown
  >({
    mutationFn: (data: WriteQuestionnaire) =>
      submitQuestionnaire(agreementId || "", token, data),
    errorTitle: t("questionnaires.errors.submitError"),
    onSuccess: (data: { pdfData: string }) => {
      const isUpdateContentMode =
        agreementStatus === "agreement_update_content";
      if (!isUpdateContentMode) {
        toast({
          title: t("questionnaires.success.submit"),
        });
        closeDialog();
      }
      setSummaryPdfBase64(data.pdfData);
      setCurrentStep("summary");
      setPersistedFormData(null);
    },
  });

  const acceptAgreementMutation = useMutationWithErrorHandling<
    undefined,
    ApiError,
    undefined,
    unknown
  >({
    mutationFn: async () => {
      await acceptAgreement(agreementId || "", token);
    },
    errorTitle: t("questionnaires.errors.acceptError"),
    onSuccess: () => {
      toast({
        title: t("questionnaires.success.accept"),
      });
      setIsAccepted(true);
      setCurrentStep("final");
      closeDialog();
    },
  });

  const cancelAgreementMutation = useMutationWithErrorHandling<
    undefined,
    ApiError,
    undefined,
    unknown
  >({
    mutationFn: async () => {
      await cancelAgreement(agreementId || "", token);
    },
    errorTitle: t("questionnaires.errors.cancelError"),
    onSuccess: () => {
      toast({
        title: t("questionnaires.success.cancel"),
      });
      setIsAccepted(false);
      setCurrentStep("final");
      closeDialog();
    },
  });

  const submitStatementsMutation = useMutationWithErrorHandling<
    undefined,
    ApiError,
    { rodo_accepted: boolean; regulations_accepted: boolean },
    unknown
  >({
    mutationFn: async (data: {
      rodo_accepted: boolean;
      regulations_accepted: boolean;
    }) => {
      await submitStatements(agreementId || "", token, data);
    },
    errorTitle: t("questionnaires.errors.submitError"),
    onSuccess: () => {
      toast({
        title: t("questionnaires.success.statementsConfirmed"),
      });
      setIsAccepted(true);
      setCurrentStep("final");
      setPersistedFormData(null);
      closeDialog();
    },
  });

  const mutations = {
    submitQuestionnaireMutation,
    acceptAgreementMutation,
    cancelAgreementMutation,
    submitStatementsMutation,
  };

  const handleSubmitFormWithoutConfirm = useCallback(
    async (data: WriteQuestionnaire) => {
      const isAmendStatements =
        questionnaire?.agreement_status === "agreement_amend_statements";

      if (isAmendStatements) {
        openDialog("submit", () =>
          submitStatementsMutation.mutate({
            rodo_accepted: data.rodo_accepted,
            regulations_accepted: data.regulations_accepted,
          }),
        );
        return;
      }

      const payload = writeQuestionnaire.parse(data);
      submitQuestionnaireMutation.mutate(payload);
    },
    [
      submitQuestionnaireMutation,
      submitStatementsMutation,
      openDialog,
      questionnaire?.agreement_status,
    ],
  );

  const handleSubmitForm = useCallback(
    async (data: WriteQuestionnaire, warningDescription?: string | null) => {
      const payload = writeQuestionnaire.parse(data);
      setPersistedFormData(payload);
      openDialog(
        "submit",
        () => submitQuestionnaireMutation.mutate(payload),
        warningDescription,
      );
    },
    [openDialog, setPersistedFormData, submitQuestionnaireMutation],
  );

  const handleAcceptAgreement = useCallback(async () => {
    openDialog("accept", () => acceptAgreementMutation.mutate(undefined));
  }, [openDialog, acceptAgreementMutation]);

  const handleCancelAgreement = useCallback(async () => {
    openDialog("reject", () => cancelAgreementMutation.mutate(undefined));
  }, [openDialog, cancelAgreementMutation]);

  return {
    handleSubmitForm,
    handleSubmitFormWithoutConfirm,
    handleAcceptAgreement,
    handleCancelAgreement,
    mutations,
  };
};
